import React, { Dispatch, ReactElement, SetStateAction, useEffect } from 'react';
import { Flex, Image, Box } from '@chakra-ui/react';
import { useParams, useRouteMatch } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import FactsLoader from '@/components/loader/Loader';
import { bannerHeight } from '@/constants/constants';
import { colors } from '@/utils/colors';
import CapabilityNode from './CapabilityNode';
import { useCreateWorkflowRendererData } from './hooks/useCreateWorkflowRenderer';
import { WorkflowRenderObj } from './types';
import { WorkflowAtom } from './states/workflowAtom';
import { WorkflowNameAtom } from './states/workflowName';
import { EndnodeAtom } from './states/endnodeAtom';
import { CapabilityFilterItem } from '../user-onboarding/types';
import useGetWorkflow from './queries/useGetWorkflow';

interface TemplateLayoutProps {
  isConfigureModalOpen: boolean;
  openConfigureModal: () => void;
  setSelectedCapability: Dispatch<SetStateAction<string>>;
  setSelectedUseCase: Dispatch<SetStateAction<CapabilityFilterItem>>;
  onConfigureModalClose: () => void;
  workflowId: string;
}

const TemplateLayout = ({
  isConfigureModalOpen,
  openConfigureModal,
  setSelectedCapability,
  setSelectedUseCase,
  onConfigureModalClose,
  workflowId,
}: TemplateLayoutProps): ReactElement => {
  const { data } = useCreateWorkflowRendererData();
  const { id } = useParams<{ id: string }>();
  const { path } = useRouteMatch();
  const { data: workflowObject, isFetching: isDetailsFetching } = useGetWorkflow(id);
  const setWorkflow = useSetRecoilState(WorkflowAtom);
  const setWorkflowName = useSetRecoilState(WorkflowNameAtom);
  const setEndColumn = useSetRecoilState(EndnodeAtom);

  useEffect(() => {
    if (!id) {
      return;
    }
    const workflowColumn = workflowObject?.workflowObject.columns || [];
    setWorkflow(workflowColumn.slice(1, workflowColumn.length - 1) || []);
    if (path.indexOf('edit') > -1) {
      setWorkflowName(workflowObject?.name || '');
    }
    setEndColumn(workflowColumn.slice(workflowColumn.length - 1)[0] || {});
  }, [id, setWorkflow, workflowObject, path, setWorkflowName, setEndColumn]);

  if (id && isDetailsFetching) {
    return (
      <Flex direction="column" w="full">
        <FactsLoader />
      </Flex>
    );
  }

  const handleCapabilityNodeClick = (nodeId: string): void => {
    setSelectedCapability(nodeId);
    openConfigureModal();
  };

  const renderWorkflow = (nodeData: WorkflowRenderObj, index: number, array: WorkflowRenderObj[]): ReactElement => {
    if (nodeData.kind === 'source') {
      return (
        <Flex direction="column">
          <CapabilityNode
            id={nodeData.id}
            handleCapabilityNodeClick={handleCapabilityNodeClick}
            nodeData={nodeData}
            isCapabilityListOpen={false}
            setIsCapabilityListOpen={() => null}
            onConfigureModalClose={onConfigureModalClose}
            isTemplate
          />
          {index + 3 !== array.length && <Image src="/assets/connection.svg" alt="connection" height="30" />}
        </Flex>
      );
    }

    return <></>;
  };

  return (
    <Box
      w="30%"
      bg="white"
      border="1px"
      borderColor="gray.200"
      borderRadius="lg"
      boxShadow={`2px 6px 8px 0px ${colors.shadows.blue600}`}
      position="fixed"
      bottom="6"
      h={`calc(100vh - ${bannerHeight + 116}px)`}
      ml="6"
      zIndex="docked"
      p="4"
    >
      <Flex
        direction="column"
        alignItems="center"
        // maxW={isConfigureModalOpen ? '40vw' : 'unset'}
        w="full"
        maxH={`calc(100vh - ${bannerHeight + 188}px)`}
        overflow="auto"
        sx={{
          '&::-webkit-scrollbar': { display: 'none' },
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none',
        }}
      >
        {data.map((d, i, arr) => renderWorkflow(d, i, arr))}
      </Flex>
    </Box>
  );
};

export default TemplateLayout;
